import {
  Card,
  CrudMode,
  DataDisplay,
  formatCurrency,
  formatDate,
  formatPercent,
  Grid,
  GridBreak,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Input, Select } from "components";
import {
  ContributorEmployment,
  ContributorEmploymentEditResource,
  ContributorEmploymentViewResource,
} from "generated/openApi";

import produce from "immer";
import "moment-timezone";

export const ContributorEmploymentPageBody = (props: {
  crudMode: CrudMode;
  employment: ContributorEmployment;
  setEmployment: (value: ContributorEmployment) => void;
  viewResource: ContributorEmploymentViewResource;
  editResource: ContributorEmploymentEditResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, smallFieldBreakpoints, xSmallFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const filteredSubPartners =
    props.viewResource.subPartners?.filter((x) => x.partnerId === props.employment.partnerId) || [];

  const partnerField = () => {
    const propertyName = pn<ContributorEmployment>("partnerId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.employment.partner?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.partners?.find((x) => x.id === props.employment.partnerId)}
        options={props.editResource.partners}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.partnerId = value?.id;
              draft.subPartnerId = undefined;
            })
          )
        }
      />
    );
  };

  const subPartnerField = () => {
    const propertyName = pn<ContributorEmployment>("subPartnerId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.employment.subPartner?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={filteredSubPartners?.find((x) => x.id === props.employment.subPartnerId)}
        options={filteredSubPartners}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.subPartnerId = value?.id;
            })
          )
        }
      />
    );
  };

  const titleField = () => {
    const propertyName = pn<ContributorEmployment>("title");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.employment.title}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.title}
        onChange={(e) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.title = e.target.value;
            })
          )
        }
      />
    );
  };

  const startDateField = () => {
    const propertyName = pn<ContributorEmployment>("startDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.employment.startDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.startDate}
        onChange={(value) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.startDate = value;
            })
          )
        }
      />
    );
  };

  const endDateField = () => {
    const propertyName = pn<ContributorEmployment>("endDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.employment.endDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.endDate}
        onChange={(value) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.endDate = value;
            })
          )
        }
      />
    );
  };

  const hourlySalaryField = () => {
    const propertyName = pn<ContributorEmployment>("hourlySalary");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatCurrency(props.employment.hourlySalary)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.hourlySalary}
        onChange={(e) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.hourlySalary = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const groupBenefitField = () => {
    const propertyName = pn<ContributorEmployment>("groupBenefit");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatPercent(props.employment.groupBenefit)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.groupBenefit}
        onChange={(e) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.groupBenefit = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const medicalBenefitField = () => {
    const propertyName = pn<ContributorEmployment>("medicalBenefit");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatCurrency(props.employment.medicalBenefit)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.medicalBenefit}
        onChange={(e) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.medicalBenefit = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const annualSalaryField = () => {
    const propertyName = pn<ContributorEmployment>("annualSalary");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatCurrency(props.employment.annualSalary)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.annualSalary}
        onChange={(e) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.annualSalary = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const annualPercentField = () => {
    const propertyName = pn<ContributorEmployment>("annualPercent");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatPercent(props.employment.annualPercent)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.employment.annualPercent}
        onChange={(e) =>
          props.setEmployment(
            produce(props.employment, (draft) => {
              draft.annualPercent = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {partnerField()}
      </Grid>

      {filteredSubPartners.length > 0 && (
        <Grid item xs={smallFieldBreakpoints}>
          {subPartnerField()}
        </Grid>
      )}

      <Grid item xs={smallFieldBreakpoints}>
        {titleField()}
      </Grid>

      <GridBreak />

      <Grid item xs={smallFieldBreakpoints}>
        {startDateField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {endDateField()}
      </Grid>

      <GridBreak />

      <Grid item xs={smallFieldBreakpoints}>
        {hourlySalaryField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {groupBenefitField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {medicalBenefitField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {annualSalaryField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {annualPercentField()}
      </Grid>
    </Grid>
  );
};
